import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { PromiseStatuses } from "../../../../utils";
import { GetAllParcelsDTO, GetAllParcelsFiltersDTO, MacrouseReviewDTO, MethodEnum, ParcelBulkUpdateDTO, ParcelDTO } from "./dataservice/dto";
import { NewParcelService } from "./dataservice/service";

interface ParcelState {
    onEdit: boolean

    // REQUEST
    parcelsBulkUpdateRequest: ParcelBulkUpdateDTO
    notificationIdToCopy: string
    notificationIdToExport: string
    updateFromCsvFile: File | null
    methodToUpdate?: MethodEnum
    startingDateOnlyConversion?: Date
    endingDateOnlyConversion?: Date
    startingDateForAll?: Date
    endingDateForAll?: Date

    // FILTERS
    getAllParcelsFilters: GetAllParcelsFiltersDTO

    // STATUS
    parcelsBulkUpdateStatus: PromiseStatuses
    copyParcelFromNotificationStatus: PromiseStatuses
    updateParcelsFromCsvStatus: PromiseStatuses
    getAllParcelsStatus: PromiseStatuses
    getParcelByIdStatus: PromiseStatuses
    exportCurrAndPrevNotStatus: PromiseStatuses
    exportCompleteNotStatus: PromiseStatuses

    // RESPONSE
    getAllParcelsResponse?: GetAllParcelsDTO
    getParcelByIdResponse?: ParcelDTO
    parcelsMacrouseDetails?: MacrouseReviewDTO
    parcelsMacrouseDetailsStatus: PromiseStatuses
}

const initialState: ParcelState = {
    onEdit: false,

    notificationIdToCopy: '',
    notificationIdToExport: '',
    updateFromCsvFile: null,

    parcelsBulkUpdateRequest: {
        parcelsBulkUpdateDTO: []
    },

    getAllParcelsFilters: {
        itemsPerPage: 0,
        page: 0,
        order: true
    },

    parcelsBulkUpdateStatus: 'idle',
    copyParcelFromNotificationStatus: 'idle',
    updateParcelsFromCsvStatus: 'idle',
    parcelsMacrouseDetailsStatus: 'idle',
    getAllParcelsStatus: 'idle',
    getParcelByIdStatus: 'idle',
    exportCompleteNotStatus: 'idle',
    exportCurrAndPrevNotStatus: 'idle'
}

export const ParcelsBulkUpdate = createAsyncThunk(
    'parcel/update/bulk',
    async (request: ParcelBulkUpdateDTO, thunkApi): Promise<void> => {
        const parcelService = NewParcelService()

        return parcelService.BulkUpdateParcel(request).catch(error => {
            throw (thunkApi.rejectWithValue(error))
        })
    },
)

export const GetAllParcels = createAsyncThunk(
    'parcel/GetAll',
    async (filters: GetAllParcelsFiltersDTO, thunkApi): Promise<GetAllParcelsDTO> => {
        const parcelService = NewParcelService()

        return parcelService.GetAllParcel(filters).catch(error => {
            throw (thunkApi.rejectWithValue(error))
        })
    },
)

export const CopyParcelFromNotification = createAsyncThunk(
    'parcel/CopyFromNotification',
    async (request: {prevNotId: string, currNotId: string}, thunkApi): Promise<void> => {
        const parcelService = NewParcelService()

        return parcelService.CopyParcelFromNotification(request.prevNotId, request.currNotId).catch(error => {
            throw (thunkApi.rejectWithValue(error))
        })
    },
)

export const UpdateParcelsFromCsv = createAsyncThunk(
    'parcel/UpdateFromCsv',
    async (data: FormData, thunkApi): Promise<void> => {
        const parcelService = NewParcelService()

        return parcelService.UpdateParcelsFromCsv(data).catch(error => {
            throw (thunkApi.rejectWithValue(error))
        })
    },
)

export const ExportCurrentAndPreviousNotifications = createAsyncThunk(
    'parcel/ExportCurrentAndPrevious',
    async (request: {currNotId: string, prevNotId?: string}, thunkApi): Promise<void> => {
        const parcelService = NewParcelService()

        return parcelService.ExportCurrentAndPreviousNotifications(request.currNotId, request.prevNotId).catch(error => {
            throw (thunkApi.rejectWithValue(error))
        })
    },
)

export const ExportCompleteNotification = createAsyncThunk(
    'parcel/ExportComplete',
    async (notId: string, thunkApi): Promise<void> => {
        const parcelService = NewParcelService()

        return parcelService.ExportCompleteNotification(notId).catch(error => {
            throw (thunkApi.rejectWithValue(error))
        })
    },
)

export const IdentificationBoardNotification = createAsyncThunk(
    'parcel/IdentificationBoardNotification',
    async (notId: string, thunkApi): Promise<void> => {
        const parcelService = NewParcelService()

        return parcelService.IdentificationBoardNotification(notId).catch(error => {
            throw (thunkApi.rejectWithValue(error))
        })
    },
)

// riepilogo macrouso particelle

export const GetMacrouseNot = createAsyncThunk(
    'parcel/GetMacrouseNot',
    async (notId: string, thunkApi): Promise<MacrouseReviewDTO> => {
        const parcelService = NewParcelService()

        return parcelService.GetMacrouseNot(notId).catch(error => {
            throw (thunkApi.rejectWithValue(error))
        })
    },
)

const ParcelSlice = createSlice({
    name: 'parcel/slice',
    initialState,
    reducers: {
        setOnEdit: (state, action) => {
            state.onEdit = action.payload
        },

        // REQUEST
        setParcelsBulkUpdateDTO: (state, action) => {
            state.parcelsBulkUpdateRequest.parcelsBulkUpdateDTO = action.payload
        },
        setParcelUpdateConversionStartingDatePerIndex: (state, action) => {
            state.parcelsBulkUpdateRequest.parcelsBulkUpdateDTO[action.payload.index].conversionStartingDate = action.payload.data
        },
        setParcelUpdateConversionEndingDatePerIndex: (state, action) => {
            state.parcelsBulkUpdateRequest.parcelsBulkUpdateDTO[action.payload.index].conversionEndingDate = action.payload.data
        },
        setParcelUpdateMethodPerIndex: (state, action) => {
            state.parcelsBulkUpdateRequest.parcelsBulkUpdateDTO[action.payload.index].method = action.payload.data
        },
        setParcelUpdateNotePerIndex: (state, action) => {
            state.parcelsBulkUpdateRequest.parcelsBulkUpdateDTO[action.payload.index].note = action.payload.data
        },
        setAllParcelMethods: (state, action) => {
            state.parcelsBulkUpdateRequest.parcelsBulkUpdateDTO.map(parcel => parcel.method = action.payload)
        },
        setOnlyConversionParcelStartingAndEndingDate: (state, action) => {
            state.parcelsBulkUpdateRequest.parcelsBulkUpdateDTO
                .filter(parcel => parcel.method === MethodEnum.IN_CONVERSIONE)
                .map(parcel => {
                    parcel.conversionStartingDate = action.payload.startingDate
                    parcel.conversionEndingDate = action.payload.endingDate
                })
        },
        setAllParcelStartingAndEndingDate: (state, action) => {
            state.parcelsBulkUpdateRequest.parcelsBulkUpdateDTO
                .map(parcel => {
                    parcel.conversionStartingDate = action.payload.startingDate
                    parcel.conversionEndingDate = action.payload.endingDate
                })
        },

        setNotificationIdToCopy: (state, action) => {
            state.notificationIdToCopy = action.payload  
        },
        setNotificationIdToExport: (state, action) => {
            state.notificationIdToExport = action.payload  
        },
        setUpdateFromCsvFile: (state, action) => {
            state.updateFromCsvFile = action.payload
        },
        setMethodToUpdate: (state, action) => {
            state.methodToUpdate = action.payload
        },
        setStartingDateOnlyConversion: (state, action) => {
            state.startingDateOnlyConversion = action.payload
        },
        setEndingDateOnlyConversion: (state, action) => {
            state.endingDateOnlyConversion = action.payload
        },
        setStartingDateForAll: (state, action) => {
            state.startingDateForAll = action.payload
        },
        setEndingDateForAll: (state, action) => {
            state.endingDateForAll = action.payload
        },

        // STATUS
        resetParcelsBulkUpdateStatus: (state) => {
            state.parcelsBulkUpdateStatus = 'idle'
        },
        resetCopyParcelFromNotificationStatus: (state) => {
            state.copyParcelFromNotificationStatus = 'idle'
        },
        resetUpdateParcelsFromCsvStatus: (state) => {
            state.updateParcelsFromCsvStatus = 'idle'
        },
        resetExportCurrAndPrevNotStatus: (state) => {
            state.exportCurrAndPrevNotStatus = 'idle'
        },
        resetExportCompleteNotStatus: (state) => {
            state.exportCompleteNotStatus = 'idle'
        },
        resetGetAllParcelsStatus: (state) => {
            state.getAllParcelsStatus = 'idle'
        },
        resetGetParcelByIdStatus: (state) => {
            state.getParcelByIdStatus = 'idle'
        },
        resetGetMacrouse: (state) => {
            state.parcelsMacrouseDetailsStatus = 'idle'
        },
    },
    extraReducers(builder) {
        builder

            // BULK UPDATE
            .addCase(ParcelsBulkUpdate.pending, (state) => {
                state.parcelsBulkUpdateStatus = 'loading'
            })
            .addCase(ParcelsBulkUpdate.fulfilled, (state) => {
                state.parcelsBulkUpdateStatus = 'successfully'
            })
            .addCase(ParcelsBulkUpdate.rejected, (state) => {
                state.parcelsBulkUpdateStatus = 'failed'
            })

            // GET ALL
            .addCase(GetAllParcels.pending, (state) => {
                state.getAllParcelsStatus = 'loading'
            })
            .addCase(GetAllParcels.fulfilled, (state, action) => {
                state.getAllParcelsStatus = 'successfully'
                state.getAllParcelsResponse = action.payload
            })
            .addCase(GetAllParcels.rejected, (state) => {
                state.getAllParcelsStatus = 'failed'
            })

            // COPY
            .addCase(CopyParcelFromNotification.pending, (state) => {
                state.copyParcelFromNotificationStatus = 'loading'
            })
            .addCase(CopyParcelFromNotification.fulfilled, (state) => {
                state.copyParcelFromNotificationStatus = 'successfully'
            })
            .addCase(CopyParcelFromNotification.rejected, (state) => {
                state.copyParcelFromNotificationStatus = 'failed'
            })

            // UPDATE FROM CSV
            .addCase(UpdateParcelsFromCsv.pending, (state) => {
                state.updateParcelsFromCsvStatus = 'loading'
            })
            .addCase(UpdateParcelsFromCsv.fulfilled, (state) => {
                state.updateParcelsFromCsvStatus = 'successfully'
            })
            .addCase(UpdateParcelsFromCsv.rejected, (state) => {
                state.updateParcelsFromCsvStatus = 'failed'
            })

            // EXPORT CURRENT AND PREVIOUS
            .addCase(ExportCurrentAndPreviousNotifications.pending, (state) => {
                state.exportCurrAndPrevNotStatus = 'loading'
            })
            .addCase(ExportCurrentAndPreviousNotifications.fulfilled, (state) => {
                state.exportCurrAndPrevNotStatus = 'successfully'
            })
            .addCase(ExportCurrentAndPreviousNotifications.rejected, (state) => {
                state.exportCurrAndPrevNotStatus = 'failed'
            })

            // EXPORT COMPLETE
            .addCase(ExportCompleteNotification.pending, (state) => {
                state.exportCompleteNotStatus = 'loading'
            })
            .addCase(ExportCompleteNotification.fulfilled, (state) => {
                state.exportCompleteNotStatus = 'successfully'
            })
            .addCase(ExportCompleteNotification.rejected, (state) => {
                state.exportCompleteNotStatus = 'failed'
            })
            //dettaglio macrouso
            .addCase(GetMacrouseNot.pending, (state) => {
                state.parcelsMacrouseDetailsStatus = 'loading'
            })
            .addCase(GetMacrouseNot.fulfilled, (state, action) => {
                state.parcelsMacrouseDetailsStatus = 'successfully'
                state.parcelsMacrouseDetails = action.payload
            })
            .addCase(GetMacrouseNot.rejected, (state) => {
                state.parcelsMacrouseDetailsStatus = 'failed'
            })
    },
})

export const {
    setOnEdit,

    // REQUEST
    setParcelsBulkUpdateDTO,
    setParcelUpdateConversionEndingDatePerIndex,
    setParcelUpdateConversionStartingDatePerIndex,
    setParcelUpdateMethodPerIndex,
    setParcelUpdateNotePerIndex,
    setNotificationIdToCopy,
    setNotificationIdToExport,
    setUpdateFromCsvFile,
    setAllParcelMethods,
    setAllParcelStartingAndEndingDate,
    setOnlyConversionParcelStartingAndEndingDate,
    setEndingDateForAll,
    setEndingDateOnlyConversion,
    setMethodToUpdate,
    setStartingDateForAll,
    setStartingDateOnlyConversion,

    // STATUS
    resetCopyParcelFromNotificationStatus,
    resetExportCompleteNotStatus,
    resetExportCurrAndPrevNotStatus,
    resetGetAllParcelsStatus,
    resetGetParcelByIdStatus,
    resetParcelsBulkUpdateStatus,
    resetUpdateParcelsFromCsvStatus,
    resetGetMacrouse
} = ParcelSlice.actions

export default ParcelSlice.reducer